import React, {useEffect, useState} from 'react';
import {Box, CardContent, CircularProgress, Tab, Tabs, Typography} from '@mui/material';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import PageWrapper from '@/components/layout/PageWrapper';
import {store} from '@/service/store';
import {useSignals} from "@preact/signals-react/runtime";

// Tab content components
import CustomerDataTab from '@/components/customers/detail/CustomerDataTab';
import CustomerVehiclesTab from '@/components/customers/detail/CustomerVehiclesTab';
import CustomerAssignmentsTab from '@/components/customers/detail/CustomerAssignmentsTab';
import CustomerDevicesTab from '@/components/customers/detail/CustomerDevicesTab';
import CustomerAssignmentsWithoutLicensePlateTab
  from '@/components/customers/detail/CustomerAssignmentsWithoutLicensePlateTab';
import MobileFriendlyCard from "@/components/common/MobileFriendlyCard.tsx";
import {AlertTriangleIcon} from "lucide-react";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`customer-tabpanel-${index}`}
      aria-labelledby={`customer-tab-${index}`}
      {...other}
      style={{ height: '100%' }}
    >
      {value === index && (
        <Box sx={{ height: '100%' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `customer-tab-${index}`,
    'aria-controls': `customer-tabpanel-${index}`,
  };
}

const CustomerDetail = () => {
  useSignals();

  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const [activeTabId, setActiveTabId] = useState('assignmentsWithoutLicense');
  const [loading, setLoading] = useState(true);
  const [customerData, setCustomerData] = useState<any>(null);
  const [hasAssignments, setHasAssignments] = useState(false);
  const [hasAssignmentsWithoutLicensePlate, setHasAssignmentsWithoutLicensePlate] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const loadCustomerData = async (forceReload = false) => {
    if (!id) return;

    setLoading(true);
    try {
      let customer = store.customers.get(id);

      // If not, load customers first
      if (!customer || forceReload) {
        await store.customers.load();
        customer = store.customers.get(id);
      }

      if (!customer) {
        setError(`Customer with ID ${id} not found`);
        setLoading(false);
        return;
      }

      // Load customer profile if not already loaded
      if (!customer.profile.data.value || Object.keys(customer.profile.data.value).length === 0) {
        await customer.profile.load();
      }

      await customer.installations.load();
      setHasAssignments(
        customer.installations.data.value.some(
          installation => installation.licenseNumber !== null
        ),
      );
      setHasAssignmentsWithoutLicensePlate(
        customer.installations.data.value.some(
          installation => installation.licenseNumber === null,
        ),
      );


      setCustomerData(customer.profile.data.value);
      setLoading(false);
    } catch (error) {
      console.error('Error loading customer data:', error);
      setError('Failed to load customer data');
      setLoading(false);
    }
  };

  const reloadCustomerData = () => {
    void loadCustomerData(true);
  }

  const tabs = [
    {
      id: 'customerData',
      label: t('pages.customerDetail.tabs.customerData'),
      component: <CustomerDataTab customerData={customerData} />,
      show: true
    },
    {
      id: 'assignmentsWithoutLicense',
      label: t('pages.customerDetail.tabs.assignmentsWithoutLicenseNumber'),
      component: <CustomerAssignmentsWithoutLicensePlateTab customerId={id!} onSuccess={reloadCustomerData}/>,
      show: hasAssignmentsWithoutLicensePlate,
      icon: <AlertTriangleIcon />
    },
    {
      id: 'assignments',
      label: t('pages.customerDetail.tabs.assignments'),
      component: <CustomerAssignmentsTab customerId={id!} />,
      show: true
    },
    {
      id: 'vehicles',
      label: t('pages.customerDetail.tabs.vehicles'),
      component: <CustomerVehiclesTab customerId={id!} />,
      show: true
    },
    {
      id: 'devices',
      label: t('pages.customerDetail.tabs.devices'),
      component: <CustomerDevicesTab customerId={id!} />,
      show: true
    }
  ];

  const visibleTabs = tabs.filter(tab => tab.show);
  const currentTabIndex = visibleTabs.findIndex(tab => tab.id === activeTabId);
  const validTabIndex = currentTabIndex >= 0 ? currentTabIndex : 0;

  useEffect(() => {
    loadCustomerData();
  }, [id]);

  const handleTabChange = (_event: React.SyntheticEvent, newIndex: number) => {
    const selectedTab = visibleTabs[newIndex];
    if (selectedTab) {
      setActiveTabId(selectedTab.id);
    }
  };

  if (loading) {
    return (
      <PageWrapper>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </PageWrapper>
    );
  }

  if (error || !customerData) {
    return (
      <PageWrapper>
        <Typography variant="h5" color="error" sx={{ mt: 4 }}>
          {error || 'Customer data not available'}
        </Typography>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper title={(customerData.businessName + ' ('+ customerData.customerNumber +')') || t('pages.customerDetail.title')}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2}}>
        <Tabs
          value={validTabIndex}
          onChange={handleTabChange}
          aria-label="customer detail tabs"
          variant="scrollable"
          scrollButtons="auto"
        >
          {visibleTabs.map((tab, index) => (
            <Tab
              key={tab.id}
              label={tab.label}
              icon={tab.icon}
              iconPosition={tab.icon ? 'start' : undefined}
              {...a11yProps(index)}
            />
          ))}
        </Tabs>
      </Box>

      <MobileFriendlyCard elevation={0} sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 250px)',
        overflow: 'hidden'
      }}>
        <CardContent sx={{
          p: 3,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden'
        }}>
          {visibleTabs.map((tab, index) => (
            <TabPanel key={tab.id} value={validTabIndex} index={index}>
              {tab.component}
            </TabPanel>
          ))}
        </CardContent>
      </MobileFriendlyCard>
    </PageWrapper>
  );
};

export default CustomerDetail;
